/**
 * Signup Funnel tab of the admin dashboard.
 *
 * Reads top-down as a goal surface: instrument board first (trials vs target
 * beside activation), then the funnel, then row-level diagnostic tabs.
 */
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import DateDropdown from '@/components/dashboard/DateDropdown'
import { Panel, PanelHeader, SegmentedControl } from '@/components/shared/layout'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { FunnelChartView, type FunnelChartStyle } from '@/components/funnels/FunnelChartViews'
import FunnelFlowChart from '@/components/funnels/FunnelFlowChart'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { HugeiconsIcon } from '@hugeicons/react'
import { InformationCircleIcon, Loading01Icon } from '@hugeicons/core-free-icons'
import { useAdminSignupFunnel } from '@/hooks/useAdminSignupFunnel'
import { reportAdminFailure, type AdminAuthor } from '@/lib/observability/admin'
import { GoalTracker } from './GoalTracker'
import { SignupJourneysTable } from './SignupJourneysTable'
import {
  BRANCH_EVENTS,
  FULL_FUNNEL_STEPS,
  KEY_MILESTONE_STEPS,
  SIGNUP_EVENT_SEQUENCE,
  buildFunnelResponse,
  buildFunnelSteps,
} from './signupFunnelSteps'

type SignupFunnelTabProps = {
  token: string | null
  /** True once the admin basic-auth modal has been submitted. */
  authorized: boolean
  /** Admin performing the lookup, for the `feature:admin` audit trail. */
  author: AdminAuthor
}

type FunnelScope = 'milestones' | 'full'

const SCOPE_OPTIONS = [
  { value: 'milestones' as const, label: 'Key milestones' },
  { value: 'full' as const, label: 'Full funnel' },
]

const STYLE_OPTIONS = [
  { value: 'stream' as const, label: 'Flow' },
  { value: 'bars' as const, label: 'Bars' },
  { value: 'list' as const, label: 'Columns' },
]

const DEVICE_OPTIONS = [
  { value: 'all', label: 'All devices' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'desktop', label: 'Desktop' },
  { value: 'tablet', label: 'Tablet' },
]

/** Collapsed-by-default disclosure for the diagnostic sections. */

/**
 * Reference list of the raw event names the funnel is built from, in flow order.
 * The charts show labels, not event types, so this is how an operator maps a step
 * back to the event to query.
 */
function EventSequenceTooltip() {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="Show tracked event sequence"
            className="text-muted-foreground transition-colors hover:text-foreground"
          >
            <HugeiconsIcon icon={InformationCircleIcon} size={14} />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" align="start" className="max-w-[320px] px-3 py-2.5">
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-background/60">
            Tracked event sequence
          </div>
          <p className="font-mono text-[10px] leading-relaxed font-normal">
            {SIGNUP_EVENT_SEQUENCE.map((slot, i) => (
              <span key={slot[0]}>
                {i > 0 && <span className="text-background/40"> → </span>}
                {slot.map((event, j) => (
                  <span key={event}>
                    {j > 0 && <span className="text-background/40"> / </span>}
                    <span className={BRANCH_EVENTS.has(event) ? 'text-background/55' : undefined}>
                      {event}
                    </span>
                  </span>
                ))}
              </span>
            ))}
          </p>
          <div className="mt-2 text-[10px] font-normal text-background/55">
            Dimmed events are exits or branches, not forward conversions.
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default function SignupFunnelTab({ token, authorized, author }: SignupFunnelTabProps) {
  // Monthly by default — the target this surface tracks is a monthly one.
  const [period, setPeriod] = useState('1d')
  const [includeTestAccounts, setIncludeTestAccounts] = useState(false)
  const [hideWorkspaceJoiners, setHideWorkspaceJoiners] = useState(true)
  const [device, setDevice] = useState('all')
  const [scope, setScope] = useState<FunnelScope>('milestones')
  const [chartStyle, setChartStyle] = useState<FunnelChartStyle>('stream')

  const { data, isLoading, isFetching, error, range } = useAdminSignupFunnel({
    token,
    period,
    includeTestAccounts,
    device: device === 'all' ? undefined : device,
    enabled: authorized,
  })

  const journeys = useMemo(() => {
    const all = data?.journeys ?? []
    return hideWorkspaceJoiners ? all.filter((j) => !j.is_workspace_joiner) : all
  }, [data?.journeys, hideWorkspaceJoiners])

  const hiddenJoiners = (data?.journeys?.length ?? 0) - journeys.length

  const funnelSteps = useMemo(() => {
    if (!data) return []
    return buildFunnelSteps(data, scope === 'full' ? FULL_FUNNEL_STEPS : KEY_MILESTONE_STEPS)
  }, [data, scope])

  const flowData = useMemo(
    () => (funnelSteps.length >= 2 ? buildFunnelResponse(funnelSteps, range) : null),
    [funnelSteps, range]
  )

  useEffect(() => {
    if (!error) return
    toast.error('Failed to load signup funnel')
    reportAdminFailure(
      'load_signup_funnel',
      error,
      author,
      { period },
      `load_signup_funnel:${period}`,
    )
  }, [error, author, period])

  return (
    <div>
      <Panel className="mb-4">
        <PanelHeader
          title="Acquisition goals"
          description="Trial volume read against activation — trials climbing without activation is not progress."
          action={
            <div className="flex flex-wrap items-center justify-end gap-3">
              {isFetching && (
                <HugeiconsIcon
                  icon={Loading01Icon}
                  size={14}
                  className="animate-spin text-muted-foreground"
                />
              )}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="funnel-test-accounts"
                  checked={includeTestAccounts}
                  onCheckedChange={(v) => setIncludeTestAccounts(v === true)}
                />
                <Label
                  htmlFor="funnel-test-accounts"
                  className="cursor-pointer text-xs font-normal text-muted-foreground"
                >
                  Test accounts
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="funnel-hide-joiners"
                  checked={hideWorkspaceJoiners}
                  onCheckedChange={(v) => setHideWorkspaceJoiners(v === true)}
                />
                <Label
                  htmlFor="funnel-hide-joiners"
                  className="cursor-pointer text-xs font-normal text-muted-foreground"
                >
                  Hide workspace joiners
                </Label>
              </div>
              <Select value={device} onValueChange={setDevice}>
                <SelectTrigger className="h-8 w-[130px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DEVICE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} className="text-xs">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DateDropdown period={period} onDateChange={setPeriod} />
            </div>
          }
        />
      </Panel>

      {isLoading && !data && (
        <Panel className="mb-4">
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
            <HugeiconsIcon icon={Loading01Icon} size={16} className="animate-spin" />
            Loading signup funnel…
          </div>
        </Panel>
      )}

      {data && (
        <>
          <GoalTracker data={data} journeys={journeys} range={range} />

          <Panel className="mb-4">
            <PanelHeader
              title={
                <span className="flex items-center gap-1.5">
                  Signup funnel
                  <EventSequenceTooltip />
                </span>
              }
              description={
                scope === 'full'
                  ? 'Every tracked step in flow order, including exits and branches.'
                  : 'Traffic through to a started trial.'
              }
              action={
                <div className="flex items-center gap-2">
                  <SegmentedControl options={STYLE_OPTIONS} value={chartStyle} onChange={setChartStyle} />
                  <SegmentedControl options={SCOPE_OPTIONS} value={scope} onChange={setScope} />
                </div>
              }
            />
            <div className="pt-4">
              {chartStyle === 'stream' ? (
                flowData && (
                  // 17 full-funnel steps can't share one viewport — scroll rather than crush them.
                  <div className="overflow-x-auto">
                    <div style={{ minWidth: Math.max(funnelSteps.length * 108, 640) }}>
                      <FunnelFlowChart data={flowData} unitNoun="unique users" height={300} />
                    </div>
                  </div>
                )
              ) : (
                <FunnelChartView
                  steps={funnelSteps}
                  entered={funnelSteps[0]?.inFunnel ?? 0}
                  style={chartStyle}
                  visitorNoun="unique users"
                />
              )}
            </div>
          </Panel>

          <Panel className="mb-3">
            <Tabs defaultValue="journeys" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="journeys">
                  User journeys
                  <Badge variant="secondary" className="ml-1.5 text-[10px]">{journeys.length}</Badge>
                </TabsTrigger>
                {(data.dropped_after_code?.length ?? 0) > 0 && (
                  <TabsTrigger value="dropped">
                    Dropped after code
                    <Badge variant="secondary" className="ml-1.5 text-[10px]">{data.dropped_after_code.length}</Badge>
                  </TabsTrigger>
                )}
                {(data.page_views?.length ?? 0) > 0 && (
                  <TabsTrigger value="pages">
                    Page views
                    <Badge variant="secondary" className="ml-1.5 text-[10px]">{data.page_views!.length}</Badge>
                  </TabsTrigger>
                )}
                {data.all_time && (
                  <TabsTrigger value="agent">Agent usage</TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="journeys">
                <SignupJourneysTable journeys={journeys} hiddenCount={hiddenJoiners} />
              </TabsContent>

              {(data.dropped_after_code?.length ?? 0) > 0 && (
                <TabsContent value="dropped">
                  <div className="overflow-hidden rounded-md border border-border/30">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/30 hover:bg-muted/30">
                          <TableHead className="text-xs font-medium">Email</TableHead>
                          <TableHead className="text-right text-xs font-medium">Attempts</TableHead>
                          <TableHead className="text-right text-xs font-medium">Requested</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.dropped_after_code.map((d, i) => (
                          <TableRow key={`${d.email}-${i}`}>
                            <TableCell className="font-mono text-sm">{d.email}</TableCell>
                            <TableCell className="text-right text-sm tabular-nums">{d.attempts}</TableCell>
                            <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
                              {new Date(d.requested_at).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
              )}

              {(data.page_views?.length ?? 0) > 0 && (
                <TabsContent value="pages">
                  <div className="overflow-hidden rounded-md border border-border/30">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/30 hover:bg-muted/30">
                          <TableHead className="text-xs font-medium">Page</TableHead>
                          <TableHead className="text-xs font-medium">Path</TableHead>
                          <TableHead className="text-right text-xs font-medium">Total Views</TableHead>
                          <TableHead className="text-right text-xs font-medium">Unique Sessions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.page_views!.map((pv, i) => (
                          <TableRow key={`${pv.path_pattern ?? pv.page_name ?? 'page'}-${i}`}>
                            <TableCell className="text-xs">{pv.page_name ?? '—'}</TableCell>
                            <TableCell className="font-mono text-xs text-muted-foreground">{pv.path_pattern ?? '—'}</TableCell>
                            <TableCell className="text-right text-xs tabular-nums">{pv.total.toLocaleString()}</TableCell>
                            <TableCell className="text-right text-xs tabular-nums">{pv.unique_sessions.toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
              )}

              {data.all_time && (
                <TabsContent value="agent">
                  <div className="flex flex-wrap gap-3">
                    {(
                      [
                        { key: 'api_key_generated', label: 'API keys generated' },
                        { key: 'mcp_prompt_copied', label: 'MCP prompt copied' },
                      ] as const
                    ).map(({ key, label }) => {
                      const stat = data.all_time![key]
                      return (
                        <div key={key} className="rounded-md border border-border/30 bg-muted/20 px-4 py-2.5">
                          <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            {label}
                          </div>
                          <div className="flex gap-4">
                            <div>
                              <div className="text-sm font-semibold tabular-nums">{stat.total.toLocaleString()}</div>
                              <div className="text-[10px] text-muted-foreground">total</div>
                            </div>
                            <div>
                              <div className="text-sm font-semibold tabular-nums">{stat.unique_users.toLocaleString()}</div>
                              <div className="text-[10px] text-muted-foreground">unique users</div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </TabsContent>
              )}
            </Tabs>
          </Panel>
        </>
      )}
    </div>
  )
}
